<template>
  <div>
    <!-- Filters bar -->
    <div class="hr-bar">
      <!-- Week picker -->
      <div class="hr-field">
        <label>Week</label>
        <div class="hr-week-picker">
          <button class="hr-btn hr-btn-sm" @click="prevWeek">
            <b-icon icon="chevron-left" font-scale="0.85" />
          </button>
          <div
            class="hr-btn"
            style="min-width: 200px; justify-content: center; cursor: default"
          >
            <b-icon icon="calendar3" font-scale="0.8" />
            {{ weekLabel }}
          </div>
          <button class="hr-btn hr-btn-sm" @click="nextWeek">
            <b-icon icon="chevron-right" font-scale="0.85" />
          </button>
        </div>
      </div>

      <!-- EA filter (admin) -->
      <div class="hr-field" v-if="role === 'admin'">
        <label>EA</label>
        <select
          class="hr-select"
          v-model="selectedEA"
          @change="loadWeeklyData"
          style="min-width: 160px"
        >
          <option value="all">All EAs</option>
          <option
            v-for="ea in assistantOptions"
            :key="ea.value"
            :value="ea.value"
            >{{ ea.text }}</option
          >
        </select>
      </div>

    </div>

    <!-- Loading -->
    <div
      v-if="isLoading"
      class="hr-card"
      style="padding: 40px; text-align: center"
    >
      <b-spinner variant="primary" />
      <p class="mt-3" style="color: #64748b">Loading weekly report…</p>
    </div>

    <template v-if="!isLoading">
      <!-- KPI cards -->
      <div class="hr-kpis">
        <div class="hr-card hr-kpi">
          <div class="k-top">
            <b-icon icon="clock" font-scale="0.9" />
            {{ role === "admin" ? "Team hours" : "Your hours" }}
          </div>
          <div class="k-val">{{ fmtH(teamTotal) }}</div>
          <div class="k-sub">{{ weekLabel }}</div>
        </div>
        <div class="hr-card hr-kpi">
          <div class="k-top">
            <b-icon icon="graph-up" font-scale="0.9" /> Billable
          </div>
          <div class="k-val" style="color: #0d9488">
            {{
              teamTotal ? Math.round((teamBill / teamTotal) * 100) + "%" : "—"
            }}
          </div>
          <div class="k-sub">{{ fmtHrs(teamBill) }}h billable</div>
        </div>
        <div class="hr-card hr-kpi">
          <div class="k-top">
            <b-icon icon="check-circle" font-scale="0.9" /> Tasks logged
          </div>
          <div class="k-val">{{ totalTasks }}</div>
          <div class="k-sub">timers this week</div>
        </div>
        <div class="hr-card hr-kpi">
          <div class="k-top">
            <b-icon icon="people" font-scale="0.9" />
            {{ role === "admin" ? "Active EAs" : "Customers" }}
          </div>
          <div class="k-val">
            {{ role === "admin" ? activeEAs : customerCount }}
          </div>
          <div class="k-sub">
            {{
              role === "admin" ? `of ${assistantOptions.length}` : "you touched"
            }}
          </div>
        </div>
      </div>

      <!-- Bar chart (admin only) -->
      <div
        class="hr-card"
        style="margin-bottom: 18px; padding-bottom: 8px"
        v-if="role === 'admin' && chartData.length > 1"
      >
        <div
          style="display: flex; justify-content: space-between; align-items: center; padding: 16px 18px 0"
        >
          <div class="hr-section-title" style="padding: 0">
            Hours by EA · by weekday
          </div>
          <div class="hr-legend">
            <span v-for="ea in perEa" :key="ea.id">
              <i :style="{ background: ea.color }" />{{ ea.initials }}
            </span>
          </div>
        </div>
        <div style="height: 230px; padding: 12px 8px 0">
          <GChart
            type="ColumnChart"
            :data="chartData"
            :options="chartOptions"
          />
        </div>
      </div>

      <!-- EA table -->
      <div class="hr-card">
        <table class="hr-table">
          <thead>
            <tr>
              <th>EA</th>
              <th>Customers</th>
              <th>Tasks</th>
              <th>Mon–Sun split</th>
              <th style="text-align: right">Billable</th>
              <th style="text-align: right">Total</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="perEa.length === 0">
              <td colspan="6">
                <div class="hr-empty">
                  <div class="e-ic">
                    <b-icon icon="bar-chart" font-scale="1.2" />
                  </div>
                  <div
                    style="font-weight: 700; color: #0f172a; margin-bottom: 4px"
                  >
                    No hours this week
                  </div>
                  <div style="font-size: 13px">Try a different EA filter.</div>
                </div>
              </td>
            </tr>
            <tr v-for="ea in perEa" :key="ea.id">
              <td>
                <div style="display: flex; gap: 10px; align-items: center">
                  <span class="hr-avatar" :style="{ background: ea.color }">{{
                    ea.initials
                  }}</span>
                  {{ ea.name }}
                </div>
              </td>
              <td class="num">{{ ea.customers }}</td>
              <td class="num">{{ ea.tasks }}</td>
              <td>
                <div
                  style="display: flex; gap: 3px; align-items: flex-end; height: 34px"
                >
                  <div
                    v-for="(m, i) in ea.byDay"
                    :key="i"
                    :title="`${dayNames[i]}: ${fmtH(m)}`"
                    :style="{
                      width: '15px',
                      borderRadius: '3px',
                      background: m ? ea.color : '#eef2f6',
                      height: `${Math.max(8, (m / ea.maxDay) * 100)}%`,
                      opacity: m ? 1 : 0.6
                    }"
                  />
                </div>
              </td>
              <td class="num" style="text-align: right">
                {{ fmtHrs(ea.billable) }}h
              </td>
              <td class="num" style="text-align: right; font-weight: 700">
                {{ fmtH(ea.total) }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </template>
  </div>
</template>

<script>
import moment from "moment";
import { getAssistantsList } from "@/apis/assistants";
import { mapState } from "vuex";
import axios from "@/config/axios";

const EA_COLORS = [
  "#2563eb",
  "#0e7490",
  "#7c3aed",
  "#c2410c",
  "#0d9488",
  "#b45309",
  "#db2777",
  "#059669",
  "#4338ca",
  "#d97706"
];

export default {
  name: "WeeklyOverview",
  data() {
    return {
      weekStart: moment().startOf("isoWeek"),
      selectedEA: "all",
      isLoading: false,
      assistantOptions: [],
      perEa: [],
      dayNames: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"],
      chartOptions: {
        backgroundColor: "transparent",
        height: 220,
        chartArea: { left: 40, right: 8, top: 8, bottom: 24 },
        legend: "none",
        colors: EA_COLORS,
        bar: { groupWidth: "55%" },
        isStacked: false,
        hAxis: {
          textStyle: { fontSize: 11, color: "#94a3b8" },
          gridlines: { count: 0 }
        },
        vAxis: {
          textStyle: { fontSize: 11, color: "#94a3b8" },
          gridlines: { color: "#eef2f6" }
        }
      }
    };
  },
  computed: {
    ...mapState("authModule", ["role", "profileUserId"]),

    weekEnd() {
      return moment(this.weekStart).endOf("isoWeek");
    },
    weekLabel() {
      const ws = this.weekStart;
      const we = this.weekEnd;
      return `Week ${ws.isoWeek()} · ${ws.format("D")}–${we.format(
        "D MMM YYYY"
      )}`;
    },
    teamTotal() {
      return this.perEa.reduce((a, e) => a + e.total, 0);
    },
    teamBill() {
      return this.perEa.reduce((a, e) => a + e.billable, 0);
    },
    totalTasks() {
      return this.perEa.reduce((a, e) => a + e.tasks, 0);
    },
    activeEAs() {
      return this.perEa.filter(e => e.total > 0).length;
    },
    customerCount() {
      return this.perEa.reduce((a, e) => a + e.customers, 0);
    },
    chartData() {
      if (this.perEa.length === 0) return [["Day"]];
      const header = ["Day", ...this.perEa.map(e => e.initials)];
      const rows = this.dayNames.map((d, i) => {
        const row = [d];
        this.perEa.forEach(e => row.push(+(e.byDay[i] / 60).toFixed(2)));
        return row;
      });
      return [header, ...rows];
    }
  },
  async mounted() {
    await this.loadAssistants();
    this.loadWeeklyData();
  },
  methods: {
    fmtH(m) {
      return `${Math.floor(m / 60)}:${String(m % 60).padStart(2, "0")}`;
    },
    fmtHrs(m) {
      return (m / 60).toFixed(1);
    },
    prevWeek() {
      this.weekStart = moment(this.weekStart).subtract(1, "week");
      this.loadWeeklyData();
    },
    nextWeek() {
      this.weekStart = moment(this.weekStart).add(1, "week");
      this.loadWeeklyData();
    },
    async loadAssistants() {
      try {
        const res = await getAssistantsList();
        this.assistantOptions = res.data.results.map((item, idx) => ({
          value: item.id,
          text: `${item.user.firstName} ${item.user.lastName}`,
          initials: `${item.user.firstName[0]}${item.user.lastName[0]}`,
          color: EA_COLORS[idx % EA_COLORS.length]
        }));
        // EAs only see their own week. Owners have no time of their own, so
        // they stay on "all", which is the EA list the API already scoped
        // to their customers.
        if (this.role !== "admin" && this.role !== "owner") {
          this.selectedEA = this.profileUserId;
        }
      } catch (e) {
        console.log(e);
      }
    },
    async loadWeeklyData() {
      this.isLoading = true;
      try {
        const fromDate = this.weekStart.format("YYYY-MM-DD");
        const toDate = this.weekEnd.format("YYYY-MM-DD");

        // Determine which EAs to fetch
        const eaList =
          this.selectedEA === "all"
            ? this.assistantOptions
            : this.assistantOptions.filter(a => a.value === this.selectedEA);

        const results = [];

        for (const ea of eaList) {
          const params = {
            completed_gte: fromDate,
            completed_lte: toDate,
            assigned_to: ea.value
          };

          try {
            const res = await axios({
              url: `/assistant-task-reports/`,
              method: "get",
              params
            });
            const tasks = res.data.results || res.data || [];
            let total = 0;
            let billable = 0;
            let taskCount = 0;
            const byDay = [0, 0, 0, 0, 0, 0, 0];
            const customerSet = new Set();

            tasks.forEach(item => {
              const day = moment(item.completedDate).isoWeekday() - 1; // Mon=0
              const mins = Math.round((item.roundedDuration || 0) / 60);
              const billMins = item.isBillable
                ? Math.round((item.roundedDuration || 0) / 60)
                : 0;
              if (day >= 0 && day < 7) byDay[day] += mins;
              total += mins;
              billable += billMins;
              taskCount += 1;
              if (item.createdFor?.id) customerSet.add(item.createdFor.id);
            });

            results.push({
              id: ea.value,
              name: ea.text,
              initials: ea.initials,
              color: ea.color,
              total,
              billable,
              tasks: taskCount,
              customers: customerSet.size || (total > 0 ? 1 : 0),
              byDay,
              maxDay: Math.max(1, ...byDay)
            });
          } catch (e) {
            // If a single EA fails, add empty row
            results.push({
              id: ea.value,
              name: ea.text,
              initials: ea.initials,
              color: ea.color,
              total: 0,
              billable: 0,
              tasks: 0,
              customers: 0,
              byDay: [0, 0, 0, 0, 0, 0, 0],
              maxDay: 1
            });
          }
        }

        this.perEa = results.filter(
          r => this.selectedEA !== "all" || r.total > 0
        );

        // Update chart colors
        this.chartOptions = {
          ...this.chartOptions,
          colors: this.perEa.map(e => e.color)
        };
      } catch (e) {
        console.log("Error loading weekly data:", e);
      } finally {
        this.isLoading = false;
      }
    },
    exportCsv() {
      const head = [
        "EA",
        "Customers",
        "Tasks",
        "Billable (h)",
        "Non-billable (h)",
        "Total (hh:mm)",
        ...this.dayNames
      ];
      const rows = this.perEa.map(e => [
        e.name,
        e.customers,
        e.tasks,
        this.fmtHrs(e.billable),
        this.fmtHrs(e.total - e.billable),
        this.fmtH(e.total),
        ...e.byDay.map(m => this.fmtHrs(m))
      ]);
      const csv = [head, ...rows]
        .map(r =>
          r
            .map(c => {
              const v = String(c ?? "");
              return /[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
            })
            .join(",")
        )
        .join("\n");
      const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "headroom_weekly_overview.csv";
      a.click();
      URL.revokeObjectURL(url);
      this.$emit("notify", "CSV downloaded");
    }
  }
};
</script>
